import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Flag, Sparkles, Loader2, Edit2, Save, X, AlertCircle, CheckCircle } from 'lucide-react';
import { reachCloseOutAPI } from '../../services/reachService';

type CloseOutStatus = 'generating' | 'draft' | 'final';

interface CloseOut {
  status: CloseOutStatus;
  generatedAt?: string;
  requestId?: string;
  content?: string;
  editedContent?: string;
  dataGates?: {
    minPulseWeeks: number;
    minTrackerWeeks: number;
    met: boolean;
  };
}

const STATUS_CHIP: Record<CloseOutStatus, { label: string; cls: string }> = {
  generating: { label: 'Generating…', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  draft: { label: 'Draft', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  final: { label: 'Final', cls: 'bg-green-50 text-green-700 border-green-200' },
};

export const CloseOutPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [closeout, setCloseout] = useState<CloseOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatedAt, setGeneratedAt] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [markingFinal, setMarkingFinal] = useState(false);
  const [gateError, setGateError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    const r = await reachCloseOutAPI.get(projectId);
    setCloseout((r.data as any).closeout ?? null);
  }, [projectId]);

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  // Poll for 90s after generate triggered
  useEffect(() => {
    if (!generatedAt) return;
    const elapsed = Date.now() - generatedAt;
    if (elapsed > 90_000) { setGeneratedAt(null); return; }
    const t = setInterval(async () => {
      await load();
      const elapsed2 = Date.now() - generatedAt;
      if (elapsed2 > 90_000) { setGeneratedAt(null); clearInterval(t); }
    }, 6000);
    return () => clearInterval(t);
  }, [generatedAt, load]);

  const generate = async () => {
    if (!projectId) return;
    setGenerating(true);
    setGateError(null);
    try {
      await reachCloseOutAPI.generate(projectId);
      setGeneratedAt(Date.now());
      await load();
    } catch (e: any) {
      const data = e?.response?.data;
      if (data?.error) setGateError(data.error);
    } finally {
      setGenerating(false);
    }
  };

  const saveEdit = async () => {
    if (!projectId) return;
    setSaving(true);
    try {
      await reachCloseOutAPI.update(projectId, { editedContent: editDraft });
      setCloseout(prev => prev ? { ...prev, editedContent: editDraft } : prev);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const markFinal = async () => {
    if (!projectId) return;
    setMarkingFinal(true);
    try {
      await reachCloseOutAPI.update(projectId, { status: 'final' });
      setCloseout(prev => prev ? { ...prev, status: 'final' } : prev);
    } finally {
      setMarkingFinal(false);
    }
  };

  if (!projectId) return null;

  const display = closeout?.editedContent ?? closeout?.content;
  const status = closeout?.status;
  const gates = closeout?.dataGates;

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
        <header className="flex items-center gap-4 border-b border-slate-200 pb-6">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
            <Flag size={22} />
          </div>
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Close-Out Report</h1>
              <p className="text-slate-500 text-sm font-medium">Summarise programme outcomes and lessons learned</p>
            </div>
            {status && (
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${STATUS_CHIP[status].cls}`}>
                {STATUS_CHIP[status].label}
              </span>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {display && !editing && status !== 'final' && (
              <>
                <button
                  onClick={() => { setEditDraft(display); setEditing(true); }}
                  className="flex items-center gap-1.5 rounded-xl h-9 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors"
                >
                  <Edit2 size={14} /> Edit
                </button>
                <button
                  onClick={markFinal}
                  disabled={markingFinal}
                  className="flex items-center gap-2 rounded-xl h-9 px-4 border border-green-200 bg-green-50 text-green-700 text-sm font-bold hover:bg-green-100 transition-colors disabled:opacity-60"
                >
                  <CheckCircle size={14} /> {markingFinal ? 'Saving…' : 'Mark as final'}
                </button>
              </>
            )}
            {editing && (
              <>
                <button
                  onClick={saveEdit}
                  disabled={saving}
                  className="flex items-center gap-2 rounded-xl h-9 px-4 bg-primary hover:bg-primary-dark text-white text-sm font-bold shadow-sm transition-colors disabled:opacity-60"
                >
                  <Save size={14} /> {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="flex items-center gap-1.5 rounded-xl h-9 px-3 border border-slate-200 bg-white text-slate-500 text-sm font-semibold hover:bg-slate-50 transition-colors"
                >
                  <X size={14} /> Cancel
                </button>
              </>
            )}
            {!editing && status !== 'final' && (
              <button
                onClick={generate}
                disabled={generating || !!generatedAt}
                className="flex items-center gap-2 rounded-xl h-9 px-5 bg-primary hover:bg-primary-dark text-white text-sm font-bold shadow-sm transition-colors disabled:opacity-60"
              >
                {generating || generatedAt ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {display ? 'Regenerate' : 'Generate close-out'}
              </button>
            )}
          </div>
        </header>

        {/* Data gate warning */}
        {gateError && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
            <AlertCircle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800">Cannot generate close-out yet</p>
              <p className="text-xs text-amber-700 mt-0.5">{gateError} — add more pulse and tracker data before generating.</p>
            </div>
          </div>
        )}

        {/* Data gates status */}
        {gates && (
          <div className="flex items-center gap-4 flex-wrap">
            <div className={`flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-lg border ${
              gates.met ? 'bg-green-50 border-green-200 text-green-700' : 'bg-slate-50 border-slate-200 text-slate-500'
            }`}>
              {gates.met ? <CheckCircle size={13} /> : <AlertCircle size={13} />}
              Min. {gates.minPulseWeeks} pulse weeks required
            </div>
            <div className={`flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-lg border ${
              gates.met ? 'bg-green-50 border-green-200 text-green-700' : 'bg-slate-50 border-slate-200 text-slate-500'
            }`}>
              {gates.met ? <CheckCircle size={13} /> : <AlertCircle size={13} />}
              Min. {gates.minTrackerWeeks} tracker weeks required
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 size={24} className="animate-spin text-slate-300" />
          </div>
        ) : (
          <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
            {!display && !generatedAt && (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Flag size={28} className="text-slate-400" />
                </div>
                <p className="text-slate-500 text-sm">No close-out report generated yet.</p>
                <p className="text-slate-400 text-xs mt-1">Requires at least half the engagement's weeks of pulse and tracker data.</p>
              </div>
            )}

            {generatedAt && !display && (
              <div className="flex items-center justify-center gap-2 py-16 text-slate-400">
                <Loader2 size={18} className="animate-spin" />
                <span className="text-sm">Generating close-out report…</span>
              </div>
            )}

            {display && (
              <div className="p-6">
                {editing ? (
                  <textarea
                    rows={20}
                    value={editDraft}
                    onChange={e => setEditDraft(e.target.value)}
                    className="w-full border border-[#e2e8f0] rounded-xl px-4 py-3 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
                  />
                ) : (
                  <>
                    {closeout?.editedContent && (
                      <div className="flex items-center gap-1.5 mb-4">
                        <Edit2 size={12} className="text-primary" />
                        <span className="text-xs text-primary font-semibold">Edited version</span>
                        <button
                          onClick={() => {
                            reachCloseOutAPI.update(projectId, { editedContent: null });
                            setCloseout(prev => prev ? { ...prev, editedContent: undefined } : prev);
                          }}
                          className="ml-2 text-xs text-slate-400 hover:text-red-400 transition-colors"
                        >
                          Revert to generated
                        </button>
                      </div>
                    )}
                    <div className="prose prose-sm max-w-none text-slate-700 leading-relaxed whitespace-pre-line">
                      {display}
                    </div>
                    {closeout?.generatedAt && (
                      <p className="mt-4 text-xs text-slate-400">
                        Generated {new Date(closeout.generatedAt).toLocaleString()}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CloseOutPage;
